import { Vector3, Object3D, Mesh } from "three";
import { AddonManager } from "../../addon/AddonManager";
import { MaterialManager } from "../../../material/MaterialManager";
import { GutterDownSpout } from "./GutterDownSpout";
import { Main } from "../../../Main";
import { Building } from "../../../building/Building";
import { IRoofSideTrimMetrics } from "../../../building/trim/IRoofSideTrimMetrics";

export class Gutter
{
	private _parent        : Object3D;

	private _gutter        : Mesh;
	private _downSpouts    : GutterDownSpout[];

	private _enabled  : boolean = true;
	private _metrics : IRoofSideTrimMetrics;

	private _temp : Vector3;

	constructor(parent:Object3D)
	{
		this._parent = parent;
		this._downSpouts = [];
		this._temp = new Vector3();
		this.init();
	}

	// ======================================================================================================
	// Init

	private init()
	{
		this.initGutter();
		this.update(true);
	}

	private initGutter()
	{
		var addonManager = AddonManager.getInstance();
		var material = MaterialManager.getInstance().trim;

		this._gutter = addonManager.createMesh("#0503 - Utility Gutter", material) as Mesh;
		this._gutter.rotation.y = Math.PI;
		this._gutter.castShadow = true;
		this._parent.add(this._gutter);
	}

	// ======================================================================================================
	// Update

	private update(force = false)
	{
		this.updateSpacing(force);
	}

	// ======================================================================================================
	// External calls/events

	// Called when roof sizes change (RoofSideTrim calls this)
	// position is the top of the gutter
	//public updateSize(position:Vector3, zFront:number, zBack:number, width:number, bottom:number, downSpoutPadding:number, wallOffset)
	public updateSize(metrics:IRoofSideTrimMetrics)
	{
		this._metrics = metrics;

		this.update();
	}

	// ======================================================================================================
	// Enabled change, show / hide

	public setEnabled(value:boolean)
	{
		this._enabled = value;

		if (this._enabled)
		{
			this.show();
		}
		else
		{
			this.hide();
		}

		this.update(true);
	}

	private show()
	{
		this._gutter.visible = true;

		for (var i = 0, ln = this._downSpouts.length; i < ln; ++i)
		{
			this._downSpouts[i].setEnabled(true);
		}
	}

	private hide()
	{
		this._gutter.visible = false;

		for (var i = 0, ln = this._downSpouts.length; i < ln; ++i)
		{
			this._downSpouts[i].setEnabled(false);
		}
	}

	// ======================================================================================================
	// Spacing

	public updateSpacing(force = false)
	{
		if (!force && !this._enabled)
		{
			return;
		}

		this.updateGutter();
		this.updateDownSpouts();
	}

	private updateGutter()
	{
		if (!this._metrics)
		{
			return;
		}

		// make it shorter so it doesn't collide with the slope trims
		// this is a rough value determined by trial and error
		var length = this._length;

		this._gutter.position.x = this._metrics.x;
		this._gutter.position.y = this._metrics.top;
		// TODO for some reason gutter's origin is at the back ?
		this._gutter.position.z = this._metrics.z_back;
		// we have to add the overhang length to the total length otherwise scaling is not going to be accurate
		let metrics = Main.getInstance().config.metrics;

		this._gutter.scale.z = (length + metrics.overhang_front + metrics.overhang_back) / 200  * AddonManager.DEFAULT_SCALE;

	}

	// ======================================================================================================
	// DownSpouts spacing

	private updateDownSpouts()
	{
		this.manageDownSpouts();
		this.alignDownSpouts();
	}

	// ======================================================================================================
	// Manage (create/delete) downspouts)

	// creates / deletes downspouts as required by the current length
	private manageDownSpouts()
	{
		if (!this._metrics)
		{
			// called too early
			return;
		}

		if (this._metrics.post_transforms)
		{
			this.manageDownSpoutsByCount(this._metrics.post_transforms.length); // basically this one runs when there's a leanto on the side
		}
		else
		{
			// TODO: this logic is duplicate from what LeanTo uses for posts
			// console.log("gl", this._length);
			var spacing = GutterDownSpout.SPACING;
			var requiredGutters = Math.max(0, Math.ceil(this._length / spacing) - 1) + 2;

			this.manageDownSpoutsByCount(requiredGutters);
		}
	}

	/**
	 * Called when lean to is disabled -> place gutters according to length
	 * @param requiredGutters the number of required gutters
	 */
	private manageDownSpoutsByCount(requiredGutters:number)
	{
		var guttersToCreate = requiredGutters - this._downSpouts.length;

		for (var i = 0; i < guttersToCreate; ++i)
		{
			var downSpout = new GutterDownSpout();
			this._parent.add(downSpout.container);
			this._downSpouts.push(downSpout);
		}

		for (i = 0; i < -guttersToCreate; ++i)
		{
			var gutterToDelete = this._downSpouts.pop();
			//gutterToDelete.hide();
			this._parent.remove(gutterToDelete.container);
			//gutterToDelete.dispose();
		}
	}

	// ======================================================================================================
	// Align downspouts (position, scale in downSpout.update)

	private alignDownSpouts()
	{
		if (!this._metrics)
		{
			// called too early
			return;
		}

		if (this._metrics.post_transforms)
		{
			this.alignDownSpoutsByPostTransforms(); // basically this one runs when there's a leanto on the side
		}
		else
		{
			this.alignDownSpoutsByLength(); // and this one when there's not
		}
	}

	private alignDownSpoutsByPostTransforms()
	{
		this.manageDownSpouts();
		var post_transforms = this._metrics.post_transforms;
		var downSpoutLength = this._downSpouts.length;
		for (var i = 0; i < downSpoutLength; ++i)
		{
			var downSpout = this._downSpouts[i];

			var post_transform = post_transforms[i];
			var post_position = post_transform.position;

			var position = this._temp;

			position.x = -this._metrics.width + 0.45;
			position.y = this._metrics.top;

			var zOffset = 0;
			var insetBays = Main.getInstance().config.building.insetBay;

			if (this._metrics.side === Building.WALLS.RIGHT)
			{
				if (insetBays.front.count.value >= 1)
				{
					zOffset += insetBays.front.length_1.value / 2;
				}
				if (insetBays.front.count.value >= 2)
				{
					zOffset += insetBays.front.length_2.value / 2;
				}
				if (insetBays.back.count.value >= 1)
				{
					zOffset -= insetBays.back.length_1.value / 2;
				}
				if (insetBays.back.count.value >= 2)
				{
					zOffset -= insetBays.back.length_2.value / 2;
				}
			}
			else if (this._metrics.side === Building.WALLS.LEFT)
			{
				if (insetBays.front.count.value >= 1)
				{
					zOffset -= insetBays.front.length_1.value / 2;
				}
				if (insetBays.front.count.value >= 2)
				{
					zOffset -= insetBays.front.length_2.value / 2;
				}
				if (insetBays.back.count.value >= 1)
				{
					zOffset += insetBays.back.length_1.value / 2;
				}
				if (insetBays.back.count.value >= 2)
				{
					zOffset += insetBays.back.length_2.value / 2;
				}
			}

			if (this._metrics.side === Building.WALLS.RIGHT || this._metrics.side === Building.WALLS.LEFT)
			{
				var zOffsetForCuts = 0;
				zOffsetForCuts -= Main.getInstance().config.building.leanto[this._metrics.side].cut_front.value / 2;
				zOffsetForCuts += Main.getInstance().config.building.leanto[this._metrics.side].cut_back.value / 2;

				if (this._metrics.side === "left")
				{
					zOffsetForCuts *= -1;
				}
				zOffset += zOffsetForCuts;
			}

			position.z = post_position + zOffset + 0.17;

			downSpout.update(position, this._metrics.gutter_bottom, this._metrics.gutter_wall_offset);
		}
	}

	private alignDownSpoutsByLength()
	{
		var downSpoutLength = this._downSpouts.length;
		for (var i = 0; i < downSpoutLength; ++i)
		{
			var downSpout = this._downSpouts[i];

			// aligned by equal distance

			var gutter_width = 0.4;

			var metrics = Main.getInstance().config.metrics;

			var zOffset = this._metrics.side ? metrics.overhang_front - metrics.overhang_back : 0;

			var length = Main.getInstance().config.building.length.value - 2;
			var pos = this._temp;

			pos.z = (-length/2 + i / (downSpoutLength-1) * length + gutter_width/2) + zOffset;
			pos.x = -this._metrics.width + 0.45;
			pos.y = this._metrics.top;

			if (i === 0)
			{
				pos.z += gutter_width/2;
			}
			else if (i === downSpoutLength - 1)
			{
				pos.z -= gutter_width/2;
			}

			downSpout.update(pos, this._metrics.gutter_bottom, this._metrics.gutter_wall_offset);
			downSpout.setEnabled(this._enabled);
		}
	}

	private get _length()
	{
		return Main.getInstance().config.building.length.value;
	}
}
